import { Suspense, useEffect, useRef } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ScreenBoundary } from './ScreenBoundary';
import { GLOBAL, INSPECTOR, PROPERTY_RAIL, railSectionOf, type Screen } from './routes';
import { ApiStatus } from './ApiStatus';
import { RailIcon } from './RailIcon';
import { PropertySelector } from './PropertySelector';
import { rememberProperty } from '../auth/lastProperty';
import type { Me } from '../api/client';
import { signOut } from '../auth/session';

/** Global destinations drawn at the top of the rail; the rest sit at its foot. */
const RAIL_TOP = ['B1-02', 'B3-17'];

/**
 * The S8B §6 shell, on the prototype's rail (founder, 2026-09-15).
 *
 * Two levels, still: a global level that exists without a property in
 * context, and a property level that only appears inside /p/:propertyId. Both
 * now live in one slim rail, as the prototype draws it — the property level
 * is four destinations (Attention, Property, Evidence, Actions), and the
 * other sections are one step away on Property instead of a row of ten tabs.
 * C7's reason for superseding the deck's rails still holds: Attention is the
 * landing and it is first.
 *
 * **Icons, with their words.** The prototype's rail is icons only. The word
 * is still every link's accessible name — what a screen reader announces and
 * what the browser suite selects on — and it appears beside the icon on hover
 * and on keyboard focus. On a phone, where there is no hover, it sits under
 * the icon.
 *
 * Nothing renders in the nav without a route behind it.
 *
 * The rail waits for the role, but the shell does not. E12-S01 requires a
 * restricted inspector to see assigned work and no other app areas, so no app
 * area is advertised before /me has said who is asking. The lists are separate
 * per role rather than one list filtered: a filter is one `||` away from
 * showing everything.
 *
 * And the Sprint 1 rule that the shell renders without a backend still holds —
 * "Connecting" is not an empty rail: it is the difference between "there is
 * nothing here" and "we have not been told yet".
 */
export function Shell({ me = null }: { me?: Me | null }) {
  const { pathname } = useLocation();

  // A new screen announces itself. After a route change, focus stayed on a
  // control that no longer existed — often the button that created the thing
  // now open — and a screen reader was told nothing had changed.
  const firstPath = useRef(true);
  useEffect(() => {
    if (firstPath.current) {
      firstPath.current = false;
      return;
    }
    const focusHeading = () => {
      const heading = document.querySelector<HTMLElement>('.shell__main h1');
      if (!heading) return false;
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
      return true;
    };
    const frame = window.requestAnimationFrame(() => {
      if (!focusHeading()) window.setTimeout(focusHeading, 400);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [pathname]);
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const unknownRole = me === null;

  /* E07-S01's other half: the landing has to have something to land on. The
     shell is the one place every property route passes through, so this
     records it once. An inspector is excluded — E12-S01 gives them assigned
     work and no other app areas. */
  useEffect(() => {
    if (propertyId && me && me.role !== 'inspector') rememberProperty(propertyId);
  }, [propertyId, me]);

  const isInspector = me?.role === 'inspector';
  const entries: Screen[] = me === null ? [] : isInspector ? INSPECTOR : GLOBAL;
  const top = isInspector ? entries : entries.filter((s) => RAIL_TOP.includes(s.id));
  const foot = isInspector ? [] : entries.filter((s) => !RAIL_TOP.includes(s.id));
  const showProperty = Boolean(propertyId) && me !== null && !isInspector;
  const section = propertyId ? railSectionOf(pathname.split(`/p/${propertyId}/`)[1] ?? '') : '';

  const globalLink = (s: Screen) => (
    <li key={s.id}>
      <NavLink
        to={s.path}
        end={s.path === '/'}
        data-screen={s.id}
        className={({ isActive }) =>
          isActive || (s.path === '/admin/users' && pathname.startsWith('/admin/'))
            ? 'active'
            : undefined
        }
      >
        <RailIcon screen={s.id} />
        <span className="rail__label">{s.label}</span>
      </NavLink>
    </li>
  );

  return (
    <div className="app">
      <nav className="rail" aria-label={isInspector ? 'Inspections' : 'Global'}>
        <span className="rail__mark" aria-hidden="true">
          <svg viewBox="0 0 32 32" width="20" height="20" focusable="false">
            <path
              d="M9 23V11l7-3 7 3v12"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinejoin="round"
            />
            <path d="M7 23h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>

        <ul className="rail__group">
          {top.map(globalLink)}
          {me === null && (
            <li className="rail__waiting">{unknownRole ? 'Not signed in' : 'Connecting…'}</li>
          )}
        </ul>

        {showProperty && (
          <nav className="rail__property" aria-label="Property">
            <ul className="rail__group">
              {PROPERTY_RAIL.map((s) => (
                <li key={s.id}>
                  {/* data-screen carries the 9B2 traceability ID into the DOM
                      so a screenshot diff can key on it rather than on copy. */}
                  <NavLink
                    to={`/p/${propertyId}/${s.path}`}
                    data-screen={s.id}
                    className={({ isActive }) =>
                      isActive || section === s.path ? 'active' : undefined
                    }
                  >
                    <RailIcon screen={s.id} />
                    <span className="rail__label">{s.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        )}

        <div className="rail__foot">
          {foot.length > 0 && <ul className="rail__group">{foot.map(globalLink)}</ul>}
          {me && (
            <button
              type="button"
              className="rail__signout"
              onClick={() => {
                signOut();
                navigate('/login', { replace: true });
              }}
            >
              <RailIcon screen="signout" />
              <span className="rail__label">Sign out</span>
            </button>
          )}
        </div>
      </nav>

      <div className="app__column">
        <header className="topbar">
          <span className="shell__mark">Premise Sense</span>
          {/* S8B §6 lists "current property identity" first under Primary
              product context, and §7 requires a persistent compact selector so
              that changing building does not mean returning to Portfolio. */}
          <PropertySelector />
        </header>

        <ApiStatus place="banner" />

        <main className="shell__main">
          {/* Screens load on first open (App.tsx). The fallback sits inside
              the frame so the rail and header stay put while it arrives. */}
          <ScreenBoundary key={pathname}>
            <Suspense
              fallback={
                <p className="basis" role="status">
                  Loading…
                </p>
              }
            >
              <Outlet />
            </Suspense>
          </ScreenBoundary>
        </main>

        {/* Developer information, out of the reading path but not hidden. */}
        <footer className="shell__foot">
          <ApiStatus place="footer" />
        </footer>
      </div>
    </div>
  );
}
